import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import {
  GetAllVendorsParams,
  IntegrationsResponse,
} from "features/integrations/integrations";
import { useNavigate } from "react-router-dom";
import {
  formatFreqAndTime,
  formatIntegrationDateTime,
} from "utils/dateUtils";
import CustomScrollbars from "../CustomScrollbars";

const headerCellStyles = {
  fontWeight: "bold",
  color: "white",
  fontSize: "1rem",
  border: "1px solid black",
  borderColor: "primary.main",
  backgroundColor: "primary.main",
  textAlign: "center",
};

const bodyCellStyles = {
  fontWeight: 300,
  fontSize: "0.875rem",
  borderRight: "1px solid red",
  borderColor: "primary.main",
  textAlign: "center",
  padding: 0.2,
  "&:last-child": { borderRight: "none" },
  "&:hover": { cursor: "pointer" },
};

const noWrapCellStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const cellTextStyles = {
  fontFamily: "Gotham, sans-serif",
  fontWeight: 300,
  fontSize: "0.875rem",
  color: "#333",
  "&:last-child": {
    borderBottom: "none",
  },
};

type IntegrationsTableProps = {
  integrations: IntegrationsResponse[];
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
  sortBy: GetAllVendorsParams["sort_by"];
  sortOrder: "asc" | "desc";
  onSortChange: (field: GetAllVendorsParams["sort_by"]) => void;
  useIntegrationTimezone?: boolean;
  pagination?: React.ReactNode;
};

export default function IntegrationsTable({
  integrations,
  isLoading,
  isFetching,
  error,
  sortBy,
  sortOrder,
  onSortChange,
  useIntegrationTimezone = true,
  pagination,
}: IntegrationsTableProps) {
  const navigate = useNavigate();

  if (error) {
    return (
      <Typography p={2} variant="h6" align="center" color="error">
        Error loading integrations
      </Typography>
    );
  }

  const safeIntegrations = (
    Array.isArray(integrations) ? integrations : []
  ).filter(
    (item): item is IntegrationsResponse =>
      item !== null &&
      item !== undefined &&
      typeof item === "object" &&
      "client" in item &&
      "integration" in item &&
      "type" in item,
  );

  const handleRowClick = (integrationId: number) => {
    navigate(`/integrations/${integrationId}`);
  };

  const columns: {
    key: GetAllVendorsParams["sort_by"] | "frequency_time";
    label: string;
  }[] = [
    { key: "client", label: "Client" },
    { key: "integration", label: "Integration" },
    { key: "type", label: "Type" },
    { key: "frequency_time", label: "Frequency/Time" },
    { key: "last_run", label: "Last Run" },
    { key: "next_run", label: "Next Run" },
    { key: "completed", label: "Completed" },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "clip",
      }}
    >
      {isLoading && <LinearProgress />}
      {!isFetching && !isLoading && safeIntegrations.length === 0 && (
        <Typography p={2} variant="h6" align="center" color="textSecondary">
          No integrations found
        </Typography>
      )}

      {safeIntegrations.length > 0 && (
        <>
        <CustomScrollbars sx={{ flex: 1, minWidth: 0, overflowX: "auto" }} trackOffsetTop={60}>
          {isFetching && <LinearProgress />}

          <Table
            stickyHeader
            sx={{
              borderColor: "divider",
              minWidth: 700,
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.key} sx={headerCellStyles}>
                    <TableSortLabel
                      sx={{
                        color: "white !important",
                        "&:hover": {
                          color: "white",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "white !important",
                        },
                        marginLeft: "18px",
                        display: "flex",
                        justifyContent: "center",
                      }}
                      active={
                        sortBy === col.key ||
                        (col.key === "frequency_time" && sortBy === "frequency")
                      }
                      direction={sortBy === col.key ? sortOrder : "asc"}
                      onClick={() =>
                        onSortChange(
                          col.key === "frequency_time" ? "frequency" : col.key,
                        )
                      }
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {safeIntegrations.map((row, index) => (
                <TableRow
                  key={index}
                  onClick={() => handleRowClick(row.id)}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      borderBottom: "none",
                    },
                  }}
                  className="hover:bg-gray-100"
                >
                  <TableCell sx={{ ...bodyCellStyles, ...noWrapCellStyle }}>
                    {row.client}
                  </TableCell>
                  <TableCell sx={bodyCellStyles}>{row.integration}</TableCell>
                  <TableCell sx={bodyCellStyles}>{row.type}</TableCell>

                  <TableCell sx={bodyCellStyles}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      {formatFreqAndTime(row).map((item, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{
                            ...cellTextStyles,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...bodyCellStyles, ...noWrapCellStyle }}>
                    {row.last_run ? (
                      <Typography variant="body2" sx={cellTextStyles}>
                        {formatIntegrationDateTime(
                          row.last_run,
                          useIntegrationTimezone ? row.timezone : undefined,
                        )}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={cellTextStyles}>
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellStyles, ...noWrapCellStyle }}>
                    {row.next_run ? (
                      <Typography variant="body2" sx={cellTextStyles}>
                        {formatIntegrationDateTime(
                          row.next_run,
                          useIntegrationTimezone ? row.timezone : undefined,
                        )}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={cellTextStyles}>
                        —
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={bodyCellStyles}>
                    {row.completed ? (
                      <CheckIcon sx={{ color: "secondary.main" }} />
                    ) : (
                      <CloseIcon sx={{ color: "red" }} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CustomScrollbars>
        </>
      )}
      {pagination}
    </Paper>
  );
}
